import { useMemo, useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { CodingWizSheet } from "./CodingWizSheet"
import { AlertCircle, Download, FileSearch, Loader2, Wand2 } from "lucide-react"
import { StatusIndicators } from "./StatusIndicators.tsx"
import { api_fetch_throws, Backup, BackupViewResponse, DeductionListJson, DeductionDetailJson, TransactionJson } from "@/api"
import { DeductionHeaderLeft, DeductionHeaderRight, AccountingStatus } from "./accounting_transaction.tsx"
import { CheckCodingHeader } from "./check_coding_header.tsx"
import { CategoryCell } from "@/deduction_table"
import { FileViewer } from "./file_viewer.tsx"
import { deductionFilesExcludingUnfiPortalCaptures, UserFiles } from "@/deduction/[id]/UserFiles.tsx"
import { TransactionPromotionField } from "./TransactionPromotionField.tsx"
import { SplitForm } from "./split_form.tsx"
import { StatusSelect } from "@/deduction_table/status_select.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { AccountingInvoiceAutocomplete } from "./AccountingInvoiceAutocomplete.tsx"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { useFetch } from "@/utils"
import { BackupTable } from "@/deduction/[id]/BackupTable.tsx"
import { backup_columns } from "@/deduction/[id]/backup_columns.tsx"
import { buildDynamicBackupColumns, WakefernBackupSummary } from "@/deduction_detail/BackupSection.tsx"
import { parseFile } from "@/api/deduction"

const INTEGRATION_LABELS: Record<string, string> = {
  sageintacct: "Sage Intacct",
  quickbooks: "QuickBooks",
  xero: "Xero",
}

interface AccountingTransactionContentProps {
  transaction: TransactionJson
}

export interface AccountingFilePaneProps {
  transaction: TransactionJson
  currentFile: { s3Uri: string; fileName: string }
  allFiles: ReturnType<typeof deductionFilesExcludingUnfiPortalCaptures>
  backupResponse: BackupViewResponse
  backupLoading: boolean
  onFileSelect: (file: ReturnType<typeof deductionFilesExcludingUnfiPortalCaptures>[number]) => void
}

function isExcelBackupUri(s3Uri: string | null | undefined): boolean {
  const lower = (s3Uri ?? "").toLowerCase()
  return lower.endsWith(".xlsx") || lower.endsWith(".xls")
}

function isKeheTransaction(transaction: TransactionJson): boolean {
  return transaction.source?.toLowerCase() === "kehe"
}

export function shouldShowParsedBackupPreview(
  transaction: Pick<TransactionJson, "backup_s3_uri">,
  currentS3Uri: string,
  backupResponse: BackupViewResponse
): boolean {
  return !!transaction.backup_s3_uri && currentS3Uri === transaction.backup_s3_uri && backupResponse.rows.length > 0
}

export function shouldOfferKeheExcelBackupParse(
  transaction: Pick<TransactionJson, "backup_s3_uri" | "source">,
  currentS3Uri: string,
  backupResponse: BackupViewResponse,
  backupLoading: boolean
): boolean {
  return (
    !!transaction.backup_s3_uri &&
    currentS3Uri === transaction.backup_s3_uri &&
    !backupLoading &&
    backupResponse.rows.length === 0 &&
    isKeheTransaction(transaction as TransactionJson) &&
    isExcelBackupUri(transaction.backup_s3_uri)
  )
}

// Offer an explicit "Parse big file" for the canonical backup when it is an unparsed PDF that the
// automatic OCR skipped for being too large (backend sets `backup_too_large`).
export function shouldOfferLargePdfBackupParse(
  transaction: Pick<TransactionJson, "backup_s3_uri">,
  currentS3Uri: string,
  backupResponse: BackupViewResponse,
  backupLoading: boolean
): boolean {
  return (
    !!transaction.backup_s3_uri &&
    currentS3Uri === transaction.backup_s3_uri &&
    !backupLoading &&
    backupResponse.rows.length === 0 &&
    !!backupResponse.backup_too_large
  )
}

function ParsedBackupPreview({
  backupResponse,
  invoiceAmount,
}: {
  backupResponse: BackupViewResponse
  invoiceAmount?: number
}) {
  const dynamicTable = backupResponse.dynamic_table
  const dynamicColumns = useMemo(() => buildDynamicBackupColumns(dynamicTable), [dynamicTable])
  const showDynamicTable = !!dynamicTable && dynamicTable.rows.length > 0 && dynamicColumns.length > 0

  return (
    <div class="h-full flex flex-col overflow-hidden">
      <div class="shrink-0 flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-900">Parsed Backup</h3>
          <p class="text-xs text-gray-500">
            {backupResponse.rows.length} parsed line{backupResponse.rows.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>
      <div class="flex-1 overflow-auto px-4 pb-4">
        {!showDynamicTable && (
          <WakefernBackupSummary backup={backupResponse.rows} invoiceAmount={invoiceAmount} />
        )}
        {showDynamicTable ? (
          <BackupTable<Record<string, string>, unknown> columns={dynamicColumns} data={dynamicTable!.rows} />
        ) : (
          <BackupTable<Backup, unknown> columns={backup_columns} data={backupResponse.rows} />
        )}
      </div>
    </div>
  )
}

function UnparsedKeheExcelBackupPreview({
  transaction,
  currentFile,
}: {
  transaction: TransactionJson
  currentFile: { s3Uri: string; fileName: string }
}) {
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)

  async function handleParseBackup() {
    if (parsing || !transaction.backup_s3_uri) return
    setParsing(true)
    setParseError(null)
    try {
      const result = await parseFile(transaction.id, transaction.backup_s3_uri, "auto")
      if (result.ok && result.value.data.success) {
        RefreshFlag.set((f: boolean) => !f)
      } else {
        setParseError(result.ok ? result.value.data.message || "Failed to parse backup file" : result.error.message)
      }
    } catch (error: unknown) {
      setParseError(error instanceof Error ? error.message : "Failed to parse backup file")
    } finally {
      setParsing(false)
    }
  }

  return (
    <div class="w-full h-full flex flex-col items-center justify-center px-6">
      <div class="max-w-md text-center">
        <h3 class="text-lg font-semibold mb-2">{currentFile.fileName}</h3>
        <p class="text-gray-600 mb-4">
          This KeHE Excel backup has not been parsed yet. Parse it to preview the backup rows and use them for custom
          splits.
        </p>
        {parseError && (
          <div class="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-left text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{parseError}</span>
          </div>
        )}
        <div class="flex justify-center gap-2">
          <Button
            onClick={handleParseBackup}
            disabled={parsing}
            aria-busy={parsing}
          >
            {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />}
            {parsing ? "Parsing..." : "Parse Backup"}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/api/_s3?s3_uri=${encodeURIComponent(currentFile.s3Uri)}`, "_blank")}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
    </div>
  )
}

// Shown when the canonical backup is a large PDF that automatic OCR skipped. Lets the user force an
// OCR parse on demand (queued), rather than auto-running expensive OCR on every big document.
function UnparsedLargePdfBackupPreview({
  transaction,
  currentFile,
}: {
  transaction: TransactionJson
  currentFile: { s3Uri: string; fileName: string }
}) {
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [queued, setQueued] = useState(false)

  async function handleParseBigFile() {
    if (parsing || !transaction.backup_s3_uri) return
    setParsing(true)
    setParseError(null)
    try {
      const result = await parseFile(transaction.id, transaction.backup_s3_uri, "azure_job")
      if (result.ok && result.value.data.success) {
        setQueued(true)
        RefreshFlag.set((f: boolean) => !f)
      } else {
        setParseError(result.ok ? result.value.data.message || "Failed to parse backup file" : result.error.message)
      }
    } catch (error: unknown) {
      setParseError(error instanceof Error ? error.message : "Failed to parse backup file")
    } finally {
      setParsing(false)
    }
  }

  return (
    <div class="w-full h-full flex flex-col items-center justify-center px-6">
      <div class="max-w-md text-center">
        <h3 class="text-lg font-semibold mb-2">{currentFile.fileName}</h3>
        <p class="text-gray-600 mb-4">
          This backup PDF is too large to parse automatically. Parse it with OCR to extract the backup rows — this
          runs in the background and may take a few minutes.
        </p>
        {parseError && (
          <div class="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-left text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{parseError}</span>
          </div>
        )}
        {queued && (
          <div class="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            Parse queued. The backup rows will appear here once processing finishes.
          </div>
        )}
        <div class="flex justify-center gap-2">
          <Button onClick={handleParseBigFile} disabled={parsing || queued} aria-busy={parsing}>
            {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />}
            {parsing ? "Queuing..." : queued ? "Queued" : "Parse big file"}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/api/_s3?s3_uri=${encodeURIComponent(currentFile.s3Uri)}`, "_blank")}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
    </div>
  )
}

export function AccountingFilePane({
  transaction,
  currentFile,
  allFiles,
  backupResponse,
  backupLoading,
  onFileSelect,
}: AccountingFilePaneProps) {
  const hasParsedBackup = shouldShowParsedBackupPreview(transaction, currentFile.s3Uri, backupResponse)
  const shouldOfferKeheParse = shouldOfferKeheExcelBackupParse(transaction, currentFile.s3Uri, backupResponse, backupLoading)
  const shouldOfferBigPdfParse = shouldOfferLargePdfBackupParse(transaction, currentFile.s3Uri, backupResponse, backupLoading)

  if (hasParsedBackup) {
    return <ParsedBackupPreview backupResponse={backupResponse} invoiceAmount={transaction.invoice_amount} />
  }

  if (shouldOfferKeheParse) {
    return <UnparsedKeheExcelBackupPreview transaction={transaction} currentFile={currentFile} />
  }

  if (shouldOfferBigPdfParse) {
    return <UnparsedLargePdfBackupPreview transaction={transaction} currentFile={currentFile} />
  }

  return (
    <FileViewer
      s3Uri={currentFile.s3Uri}
      fileName={currentFile.fileName}
      allFiles={allFiles.map((file: any) => ({
        display_name: file.display_name,
        s3_uri: file.s3_uri,
        actor_name: file.actor_name,
        date: file.date,
      }))}
      onFileSelect={onFileSelect}
    />
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("accounting backup preview state", () => {
    const parsedBackup: BackupViewResponse = { rows: [{ id: "backup-row-1" } as Backup], dynamic_table: null }
    const emptyBackup: BackupViewResponse = { rows: [], dynamic_table: null }

    it("shows parsed backup preview for the selected canonical backup file", () => {
      expect(
        shouldShowParsedBackupPreview(
          { backup_s3_uri: "s3://bucket/backup.xlsx" },
          "s3://bucket/backup.xlsx",
          parsedBackup
        )
      ).toBe(true)
    })

    it("offers parse for unparsed KeHE Excel canonical backups", () => {
      expect(
        shouldOfferKeheExcelBackupParse(
          { source: "kehe", backup_s3_uri: "s3://bucket/backup.xlsx" },
          "s3://bucket/backup.xlsx",
          emptyBackup,
          false
        )
      ).toBe(true)
    })

    it("does not offer parse for non-KeHE Excel backups", () => {
      expect(
        shouldOfferKeheExcelBackupParse(
          { source: "unfi", backup_s3_uri: "s3://bucket/backup.xlsx" },
          "s3://bucket/backup.xlsx",
          emptyBackup,
          false
        )
      ).toBe(false)
    })

    it("offers Parse big file when backend flags an unparsed large PDF backup", () => {
      const largeBackup: BackupViewResponse = { rows: [], dynamic_table: null, backup_too_large: true }
      expect(
        shouldOfferLargePdfBackupParse(
          { backup_s3_uri: "s3://bucket/backup.pdf" },
          "s3://bucket/backup.pdf",
          largeBackup,
          false
        )
      ).toBe(true)
    })

    it("does not offer Parse big file for normal-sized backups", () => {
      expect(
        shouldOfferLargePdfBackupParse(
          { backup_s3_uri: "s3://bucket/backup.pdf" },
          "s3://bucket/backup.pdf",
          emptyBackup,
          false
        )
      ).toBe(false)
    })
  })
}

export function AccountingTransactionContent({ transaction }: AccountingTransactionContentProps) {
  const [selectedFile, setSelectedFile] = useState<{ s3Uri: string; fileName: string } | null>(null)
  const [codingWizOpen, setCodingWizOpen] = useState(false)
  const orgSettings = useOrgSettings()
  const refreshFlag = RefreshFlag.use()
  const { data: backupResponse, loading: backupLoading } = useFetch(
    () => api_fetch_throws<BackupViewResponse>(`/deduction/${transaction.id}/backup?include_raw=true`),
    [transaction.id, refreshFlag],
    { rows: [], dynamic_table: null }
  )

  const integrationLabel = orgSettings.accounting_integration
    ? INTEGRATION_LABELS[orgSettings.accounting_integration.toLowerCase()] || orgSettings.accounting_integration
    : null

  return (
    <div class="w-full">
      <div class="flex items-end justify-between mt-1 mb-3 mx-4 gap-4">
        <DeductionHeaderLeft deduction={transaction as DeductionListJson}>
          <AccountingStatus status={transaction.accounting_status} />
          {["deduction", "invoice"].includes(transaction.kind) && transaction.kind !== "repayment" && (
            <StatusSelect deduction={transaction as DeductionListJson} onStatusChange={() => RefreshFlag.set((f: boolean) => !f)} />
          )}
        </DeductionHeaderLeft>
        <DeductionHeaderRight deduction={transaction as DeductionListJson}>
          <div class="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <Button variant="outline" size="sm" class="gap-1" onClick={() => setCodingWizOpen(true)}>
              <Wand2 className="h-4 w-4" />
              CodingWiz
            </Button>
            <CheckCodingHeader number={transaction?.check_number} transaction_id={transaction.id} class="w-1/2" />
          </div>
        </DeductionHeaderRight>
      </div>
      <div class="flex w-full" style="height: calc(100vh - 200px)">
        <div class="overflow-auto flex-1">
          <div class="mx-4 mb-4">
            <div class="text-gray-500 text-sm mb-1">Category</div>
            <CategoryCell deduction={transaction as DeductionListJson} className="mt-1" />
            {(transaction as DeductionListJson).category_description && (
              <div class="mt-2 text-sm text-gray-700">{(transaction as DeductionListJson).category_description}</div>
            )}
          </div>
          {transaction.kind === "invoice" && orgSettings.accounting_integration && (
            <div class="mx-4 mb-4">
              <div class="text-gray-500 text-sm mb-1">{integrationLabel} Invoice</div>
              <AccountingInvoiceAutocomplete
                deductionId={transaction.id}
                onSuccess={() => RefreshFlag.set((f: boolean) => !f)}
              />
            </div>
          )}
          <TransactionPromotionField transaction={transaction} className="mx-4 mb-4" />
          <SplitForm transaction={transaction} />
          <StatusIndicators transaction={transaction} />
          <div class="mx-4 mt-8 min-w-80">
            <UserFiles deduction={transaction as any as DeductionDetailJson} />
          </div>
        </div>
        {(() => {
          const allFiles = deductionFilesExcludingUnfiPortalCaptures(transaction as DeductionListJson)
          if (allFiles.length === 0) return null

          // Prioritize backup file if it exists, otherwise use the first file
          const defaultFile = transaction.backup_s3_uri
            ? allFiles.find((file: any) => file.s3_uri === transaction.backup_s3_uri) || allFiles[0]
            : allFiles[0]

          const currentFile = selectedFile || {
            s3Uri: defaultFile.s3_uri,
            fileName: defaultFile.display_name,
          }

          return (
            <div class="w-1/2 border-l border-gray-200 flex flex-col">
              <AccountingFilePane
                transaction={transaction}
                currentFile={currentFile}
                allFiles={allFiles}
                backupResponse={backupResponse}
                backupLoading={backupLoading}
                onFileSelect={(file: any) => {
                  setSelectedFile({
                    s3Uri: file.s3_uri,
                    fileName: file.display_name,
                  })
                }}
              />
            </div>
          )
        })()}
      </div>
      <CodingWizSheet
        open={codingWizOpen}
        onOpenChange={setCodingWizOpen}
        deductionId={transaction.id}
        onComplete={() => RefreshFlag.set((f: boolean) => !f)}
      />
    </div>
  )
}
